package com.evoapps.evoshift.domain.usecase.weather

import com.evoapps.evoshift.domain.model.DayWeather
import com.evoapps.evoshift.domain.model.Workplace
import com.evoapps.evoshift.domain.repository.WeatherRepository
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flowOf
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus

/**
 * SH-226: a workplace's weather for a range of dates, live. Nothing (an empty map, no listener)
 * when the workplace has weather off or no location: the server writes none then either.
 */
class ObserveWorkplaceWeatherUseCase(private val repository: WeatherRepository) {

    operator fun invoke(workplace: Workplace?, from: LocalDate, to: LocalDate): Flow<Map<LocalDate, DayWeather>> =
        if (workplace == null || !hasWeather(workplace) || to < from) flowOf(emptyMap())
        else repository.observeDays(workplace.id, from, to)

    /** The forecast strip and the roster headers: [today] and the [FORECAST_DAYS] - 1 days after it. */
    fun upcoming(workplace: Workplace?, today: LocalDate): Flow<Map<LocalDate, DayWeather>> =
        invoke(workplace, today, today.plus(DatePeriod(days = FORECAST_DAYS - 1)))

    companion object {
        /** How far ahead the server keeps a forecast (functions/weather.js writes today + 9 days). */
        const val FORECAST_DAYS = 10

        /** Whether the workplace gets weather: switched on and it has a location to fetch it for. */
        fun hasWeather(workplace: Workplace): Boolean = workplace.settings.weatherEnabled && workplace.location != null

        /** The upcoming days, in date order, from [today] on; days without a forecast are left out. */
        fun upcomingDays(days: Map<LocalDate, DayWeather>, today: LocalDate, count: Int = FORECAST_DAYS): List<DayWeather> =
            days.values.filter { it.date >= today && it.forecast != null }.sortedBy { it.date }.take(count)
    }
}
